// Helpers for the Home page (US 9373): due groups, task states and links
import Vue from 'vue';
const moment = require('moment');

// Project types whose tasks are grouped by the element they are about
const ASSET_PROJECT_TYPES = ['update', 'validation', 'control', 'risk_assessment', 'bia_assessment'];

export const ASSET_ICONS = { process: 'cube', activity: 'cubes', system: 'desktop', contract: 'file-signature' };

// Tabs in "My work" and which task states they show
export const TABS = {
  all: null,
  todo: ['in_progress', 'waiting_on_client', 'not_started', 'returned'],
  ready: ['ready_to_test'],
  sign_off: ['sign_off'],
  rejected: ['rejected']
};

// Focus now: the most urgent first, at most 3 cards
const FOCUS_ORDER = ['overdue', 'rejected', 'ready_to_test', 'sign_off', 'today'];

export function daysUntil(date, today) {
  if (!date) {
    return null;
  }
  return moment(date, 'YYYY-MM-DD').diff(moment(today, 'YYYY-MM-DD'), 'days');
}

export function dueGroup(task, today) {
  // Sign-offs and rejected validations wait for the user now, whatever the project's due date
  if (task.state === 'sign_off' || task.kind === 'validation') {
    return 'today';
  }
  const days = daysUntil(task.due_date, today);
  if (days === null) {
    return 'later';
  }
  if (days < 0) {
    return 'overdue';
  }
  if (days === 0) {
    return 'today';
  }
  // The rest of this week, up to and including Sunday
  const endOfWeek = moment(today, 'YYYY-MM-DD').endOf('isoWeek');
  return moment(task.due_date, 'YYYY-MM-DD').isSameOrBefore(endOfWeek, 'day') ? 'week' : 'later';
}

// The heading a task is listed under within its project: the control objective on control assessments,
// the element on update, validation, control and assessment projects; none on security measure projects
function subGroup(task) {
  const type = task.project ? task.project.type : null;
  if (type === 'control_assessment' && task.control_objective) {
    const objective = task.control_objective;
    return { key: 'o-' + objective.id, kind: 'objective', title: objective.name, icon: 'crosshairs', order: objective.order, id: objective.id };
  }
  if (ASSET_PROJECT_TYPES.includes(type) && task.asset) {
    return { key: 'a-' + task.asset_type + '-' + task.asset_id, kind: 'asset', title: task.asset, icon: ASSET_ICONS[task.asset_type] || 'cube', order: 0, id: 0 };
  }
  return null;
}

// The order of the project's task list: by the order set there (none set comes last), then as added
function byListOrder(a, b) {
  const orderOf = item => (item.order === null || item.order === undefined ? Number.MAX_SAFE_INTEGER : item.order);
  return (orderOf(a) - orderOf(b)) || (Number(a.id) || 0) - (Number(b.id) || 0);
}

// "My work" by project, soonest due first (rejected validations have no project and come first),
// then by control objective or element. The due date is the project's.
export function projectTree(tasks) {
  const projects = {};
  tasks.forEach(task => {
    const key = task.project ? 'p-' + task.project.id : 'validations';
    if (!projects[key]) {
      projects[key] = { key, project: task.project, due_date: null, count: 0, ungrouped: [], groups: {} };
    }
    const project = projects[key];
    project.count++;
    if (task.project && task.due_date && (!project.due_date || task.due_date < project.due_date)) {
      project.due_date = task.due_date;
    }
    const sub = subGroup(task);
    if (!sub) {
      project.ungrouped.push(task);
      return;
    }
    if (!project.groups[sub.key]) {
      project.groups[sub.key] = Object.assign(sub, { tasks: [] });
    }
    project.groups[sub.key].tasks.push(task);
  });

  return Object.values(projects)
    .map(project => Object.assign(project, {
      ungrouped: project.ungrouped.sort(byListOrder),
      groups: Object.values(project.groups)
        .sort((a, b) => byListOrder(a, b) || a.title.localeCompare(b.title))
        .map(group => Object.assign(group, { tasks: group.tasks.sort(byListOrder) }))
    }))
    .sort((a, b) => {
      if (!a.project || !b.project) {
        return a.project ? 1 : (b.project ? -1 : 0);
      }
      return String(a.due_date || '9999').localeCompare(String(b.due_date || '9999')) || a.project.title.localeCompare(b.project.title);
    });
}

// How soon a due date is, as shown next to a project in "My work"
export function dueTone(date, today) {
  const days = daysUntil(date, today);
  if (days === null) {
    return '';
  }
  if (days < 0) {
    return 'is-red';
  }
  return days === 0 ? 'is-amber' : '';
}

export function dueText(vm, date, today) {
  const days = daysUntil(date, today);
  if (days === null) {
    return '–';
  }
  if (days < 0) {
    return vm.$tc('home.due.days_ago', -days, { count: -days });
  }
  if (days === 0) {
    return vm.$t('home.groups.today');
  }
  if (days === 1) {
    return vm.$t('home.due.tomorrow');
  }
  return moment(date, 'YYYY-MM-DD').format(days < 7 ? 'ddd D MMM' : 'D MMM');
}

export function focusKind(task, today) {
  if (task.state === 'rejected') {
    return 'rejected';
  }
  if (task.state === 'sign_off') {
    return 'sign_off';
  }
  // Newly delivered documentation is the news, even when the task is late
  if (task.state === 'ready_to_test' && task.is_new) {
    return 'ready_to_test';
  }
  const days = daysUntil(task.due_date, today);
  if (days !== null && days < 0 && task.kind !== 'validation') {
    return 'overdue';
  }
  if (task.state === 'ready_to_test') {
    return 'ready_to_test';
  }
  if (days === 0) {
    return 'today';
  }
  return null;
}

export function focusTasks(tasks, today) {
  const sorted = tasks
    .map(task => ({ task, kind: focusKind(task, today) }))
    .filter(item => item.kind)
    .sort((a, b) => {
      const order = FOCUS_ORDER.indexOf(a.kind) - FOCUS_ORDER.indexOf(b.kind);
      if (order) {
        return order;
      }
      // New changes first; among overdue tasks the most recently due first, so years-old backlog doesn't crowd out the rest
      const news = (b.task.is_new ? 1 : 0) - (a.task.is_new ? 1 : 0);
      return news || String(b.task.due_date).localeCompare(String(a.task.due_date));
    });

  // One card per kind first, so an overdue backlog doesn't hide deliveries and sign-offs; then fill up
  const picked = [];
  FOCUS_ORDER.forEach(kind => {
    const item = sorted.find(candidate => candidate.kind === kind);
    if (item && picked.length < 3) {
      picked.push(item);
    }
  });
  sorted.forEach(item => {
    if (picked.length < 3 && !picked.includes(item)) {
      picked.push(item);
    }
  });

  return picked.sort((a, b) => FOCUS_ORDER.indexOf(a.kind) - FOCUS_ORDER.indexOf(b.kind));
}

// Tag shown on a task row
export function taskTag(task) {
  if (task.kind === 'validation') {
    return { key: 'rejected_validation', color: 'red' };
  }
  if (task.state === 'rejected') {
    return { key: 'rejected', color: 'red' };
  }
  if (task.state === 'sign_off') {
    return { key: 'sign_off', color: 'teal' };
  }
  if (task.state === 'ready_to_test') {
    return { key: 'ready_to_test', color: 'violet' };
  }
  if (task.kind === 'data' || task.kind === 'statement') {
    return { key: 'data_collection', color: 'blue' };
  }
  if (task.project && task.project.type === 'control_assessment') {
    return task.test_phase === 'operating_effectiveness'
      ? { key: 'oe_testing', color: 'violet' }
      : { key: 'design_implementation', color: 'blue' };
  }
  return { key: 'type_' + (task.project ? task.project.type : 'other'), color: 'grey' };
}

// Rejected validations open the element page on the tab of the validation (as in RejectedValidations.vue)
export function validationRoute(validation) {
  const type = validation.type;
  const slug = validation.relation ? validation.relation.slug : null;
  let tabIndex = 0;

  switch (validation.relation ? validation.relation.who_am_i : '') {
    case 'App\\Process\\Models\\Process':
      if (['legal_basis', 'information_obligation', 'risk_profile', 'confidentiality', 'integrity', 'availability'].includes(type)) {
        tabIndex = 1;
      }
      if (type === 'risk_scenario') {
        tabIndex = 2;
      }
      return { name: 'processes.view', params: { slug, tabIndex, validation: type } };
    case 'App\\System\\Models\\System':
      if (type === 'risk_scenario') {
        tabIndex = 2;
      }
      return { name: 'systems.view', params: { slug, tabIndex, validation: type } };
    case 'App\\Activity\\Models\\Activity':
      if (type === 'mapping_description') {
        tabIndex = 1;
      }
      if (['retention_rule', 'integrity_check', 'processing_type'].includes(type)) {
        tabIndex = 2;
      }
      if (type === 'risk_scenario') {
        tabIndex = 3;
      }
      return { name: 'activities.view', params: { slug, tabIndex, validation: type } };
    case 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty':
      if (type === 'risk_scenario') {
        tabIndex = 3;
      }
      if (type === 'third_party') {
        tabIndex = 8;
      }
      return { name: 'contracts.view', params: { slug, tabIndex, validation: type } };
    case 'App\\RiskScenario\\Models\\RiskScenario':
      if (type === 'risk_scenario') {
        tabIndex = 4;
      }
      return { name: 'risk_scenarios.view', params: { slug, tabIndex, validation: type } };
    case 'App\\GenericControl\\Models\\GenericControl':
      if (type === 'generic_control') {
        tabIndex = 4;
      }
      return { name: 'generic_controls.view', params: { slug, tabIndex, validation: type } };
    case 'App\\Maintenance\\Policy\\Models\\Policy':
      if (type === 'policy') {
        tabIndex = 2;
      }
      return { name: 'policies.view', params: { slug, tabIndex, validation: type } };
    default:
      return null;
  }
}

// A rejected validation as a row in "My work"
export function validationRow(validation, today) {
  return {
    id: 'validation-' + validation.id,
    kind: 'validation',
    state: 'rejected',
    title: validation.relation ? validation.relation.name : '',
    validation,
    due_date: today,
    project: null,
    new_messages: 0,
    files: 0
  };
}

// Display title of a task, as the task list modal shows it: update and validation tasks have
// translated titles, risk assessment tasks a fixed label
const ASSESSMENT_CODES = ['business_risk_assessment', 'privacy_risk_assessment', 'system_impact_assessment'];

export function taskTitle(vm, task) {
  if (!task) {
    return '';
  }
  if (task.kind === 'validation') {
    return task.title;
  }
  // The management statement or system description of a control assessment (US 9394)
  if (task.kind === 'statement') {
    return vm.$t('projects.measures.' + task.field + '_title');
  }
  if (ASSESSMENT_CODES.includes(task.code)) {
    return vm.$t('maintenance.assessments.system_impact_assessment_label');
  }
  const type = task.project ? task.project.type : null;
  if ((type === 'update' || type === 'validation') && task.name) {
    const key = 'tasks.values.' + Vue.filter('slugify')(task.name);
    return vm.$te(key) ? vm.$t(key) : task.name;
  }
  return task.title;
}
